import Vue from 'vue'

import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import store from '@/store'

import {
  AsylumAssystantSectionActions,
  AsylumAssystantSectionGetters,
  AsylumAssystantSectionMutations,
} from './party-edit/asylum-assystant-section'
import {
  BackgroundSectionActions,
  BackgroundSectionGetters,
  BackgroundSectionMutations,
} from './party-edit/background-section'
import {
  FamiliesSectionActions,
  FamiliesSectionGetters,
  FamiliesSectionMutations,
} from './party-edit/families-section'
import {
  HistorySectionActions,
  HistorySectionGetters,
  HistorySectionMutations,
} from './party-edit/history-section'
import {
  OverviewSectionActions,
  OverviewSectionGetters,
  OverviewSectionMutations,
  OverviewSectionState,
} from './party-edit/overview-section'
import {
  PESharedActions,
  PESharedGetters,
  PESharedMutations,
} from './party-edit/shared'
import {
  PersonalDataSectionActions,
  PersonalDataSectionGetters,
  PersonalDataSectionMutations,
} from './party-edit/personal-data-section'
import {
  SurveysSectionActions,
  SurveysSectionGetters,
  SurveysSectionMutations,
  SurveysSectionState,
} from './party-edit/surveys-section'
import {
  fullBooleanReducer,
  fullPartySectionSetter,
  processPartyEditRequirements,
} from './party-edit/helpers/party-edit'
import { PESharedQuickEditState } from './party-edit/shared/pe-shared-quick-edit'

const virginPartyEdit = {
  partyPermissions: {
    access: false,
    update: false,
  },
  party: {},
  party_survey_session: null,
  partyDocuments: [],
  photoBlob: null,
  photoOriginalBlob: null,
  asylumAssystant: {},
  partyLoading: false,
  genericSectionLoading: true,
  quickEdit: PESharedQuickEditState,
  partySections: {},
  surveys: SurveysSectionState,
  overview: OverviewSectionState,
}
export const PartyEditState = {
  partyEdit: { ...virginPartyEdit },
}

export const PartyEditActions = {
  peGetPartySection: async ({ commit, dispatch }, { id }) => {
    commit('peSetPartyLoading', true)
    commit('peSetGenericSectionLoading', true)
    return dispatch('peCheckPartyPermissions', { id }).
      then(_ => axiosRequest.
        get(`/parties/${id}`).
        then(({ data }) => {
          commit('peSetParty', processPartyEditRequirements(data?.party))
          commit('peSetPartyLoading', false)
          commit('peSetGenericSectionLoading', false)
          dispatch('peProcessPhoto')
          dispatch('peFetchPartyDocuments')
        }).
        catch(handleError))
  },
  peCheckPartyPermissions: async ({ commit }, { id }) => {
    return axiosRequest.
      get(`/parties/${id}/permissions`).
      then(({ data }) => {
        commit('peSetPartyPermissions', data)
      }).
      catch(handleError)
  },
  peLockPartySections: ({ commit, state }) => {
    const partySections = state.partyEdit.partySections
    commit('peSetPartySections', fullPartySectionSetter(partySections, true))
  },
  peResetPartyEdit: ({ commit }) => commit('peSetPartyEdit', { ...virginPartyEdit }),
  peSetCollapsedAllCards: _ => {},
  ...AsylumAssystantSectionActions,
  ...PersonalDataSectionActions,
  ...FamiliesSectionActions,
  ...BackgroundSectionActions,
  ...OverviewSectionActions,
  ...HistorySectionActions,
  ...PESharedActions,
  ...SurveysSectionActions,
}

export const PartyEditGetters = {
  genericSectionLoading: state => state.partyEdit.genericSectionLoading,
  pePartyLoading: state => state.partyEdit.partyLoading,
  peGenericSectionLoading: state => state.partyEdit.genericSectionLoading,
  peParty: state => state.partyEdit.party || {},
  peAnyUnlockedSections: state => !fullBooleanReducer(state.partyEdit.partySections),
  peUserCanUpdate: state => state.partyEdit.partyPermissions.update,
  peUserCanAccess: state => state.partyEdit.partyPermissions.access,
  ...AsylumAssystantSectionGetters,
  ...PersonalDataSectionGetters,
  ...FamiliesSectionGetters,
  ...BackgroundSectionGetters,
  ...OverviewSectionGetters,
  ...HistorySectionGetters,
  ...PESharedGetters,
  ...SurveysSectionGetters,
}

export const PartyEditMutations = {
  peSetPartyEdit: (state, partyEdit) => { state.partyEdit = partyEdit },
  peSetParty: (state, party) => { state.partyEdit.party = party },
  pePatchParty: (state, patch) => {
    for (const key of Object.keys(patch)) Vue.set(state.partyEdit.party, key, patch[key])

    // TODO: This really shouldn't be here .. these calls should be made from actions, mutations should never call other
    // mutations. But all the Party Edit actions that partially update the Party data call this mutation in order to
    // persist the changes, and it would be extremely cumbersome to update dozens of actions for each to call the
    // following two mutations, so the Party data is also updated on Records and Legal Cases.
    //
    // Ideally there should be a central Party Store that the Record and Legal Case stores would reference to fetch the
    // Parties data they need and that Party Edit would commit the changes made to a Party.
    //
    // This is truly a hack, and a store refactoring will need to happen to get rid of this.

    store.commit('updateExistingRecordPartyModel', state.partyEdit.party, { root: true })
    store.commit(`${LEGAL_CASE_NAMESPACE}/updateLegalCaseParty`, state.partyEdit.party, { root: true })
  },
  peSetPartyLoading: (state, value) => { state.partyEdit.partyLoading = value },
  peSetGenericSectionLoading: (state, value) => { state.partyEdit.genericSectionLoading = value },
  peSetPartySections: (state, value) => { state.partyEdit.partySections = value },
  pePatchCharacteristic: (state, { characteristic, value }) => {
    const index = state.partyEdit.party[characteristic]?.findIndex(i => i.id === value.id)
    Vue.set(state.partyEdit.party[characteristic], index, value)
  },
  peSetPartyPermissions: (state, { update, access }) => {
    Vue.set(state.partyEdit.partyPermissions, 'access', access)
    Vue.set(state.partyEdit.partyPermissions, 'update', update)
  },
  ...AsylumAssystantSectionMutations,
  ...PersonalDataSectionMutations,
  ...FamiliesSectionMutations,
  ...BackgroundSectionMutations,
  ...OverviewSectionMutations,
  ...HistorySectionMutations,
  ...PESharedMutations,
  ...SurveysSectionMutations,
}
